import React from 'react';
import { Plus, Save, Trash2, Eye, Download, Settings, User, LogOut } from 'lucide-react';
import Button from './Button';

const ButtonShowcase: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto p-8 space-y-12">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Button Design System</h1>
        <p className="text-lg text-gray-600">Unified button styles across the application</p>
      </div>

      {/* Primary Buttons */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Primary Buttons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Small</h3>
            <Button variant="primary" size="sm" leftIcon={<Plus className="h-3 w-3" />}>
              Add Item
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Medium</h3>
            <Button variant="primary" size="md" leftIcon={<Save className="h-4 w-4" />}>
              Save Changes
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Large</h3>
            <Button variant="primary" size="lg" leftIcon={<Plus className="h-5 w-5" />}>
              Create New
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Extra Large</h3>
            <Button variant="primary" size="xl" leftIcon={<Plus className="h-6 w-6" />}>
              Get Started
            </Button>
          </div>
        </div>
      </section>

      {/* Secondary Buttons */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Secondary Buttons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Small</h3>
            <Button variant="secondary" size="sm" leftIcon={<Settings className="h-3 w-3" />}>
              Settings
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Medium</h3>
            <Button variant="secondary" size="md" leftIcon={<User className="h-4 w-4" />}>
              Profile
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Large</h3>
            <Button variant="secondary" size="lg" leftIcon={<Download className="h-5 w-5" />}>
              Download
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Extra Large</h3>
            <Button variant="secondary" size="xl" leftIcon={<Settings className="h-6 w-6" />}>
              Configure
            </Button>
          </div>
        </div>
      </section>

      {/* Outline Buttons */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Outline Buttons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Small</h3>
            <Button variant="outline" size="sm" leftIcon={<Eye className="h-3 w-3" />}>
              View
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Medium</h3>
            <Button variant="outline" size="md" leftIcon={<Eye className="h-4 w-4" />}>
              Preview
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Large</h3>
            <Button variant="outline" size="lg" leftIcon={<Eye className="h-5 w-5" />}>
              View Details
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Extra Large</h3>
            <Button variant="outline" size="xl" leftIcon={<Eye className="h-6 w-6" />}>
              Explore
            </Button>
          </div>
        </div>
      </section>

      {/* Danger Buttons */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Danger Buttons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Small</h3>
            <Button variant="danger" size="sm" leftIcon={<Trash2 className="h-3 w-3" />}>
              Delete
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Medium</h3>
            <Button variant="danger" size="md" leftIcon={<Trash2 className="h-4 w-4" />}>
              Remove
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Large</h3>
            <Button variant="danger" size="lg" leftIcon={<Trash2 className="h-5 w-5" />}>
              Delete All
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Extra Large</h3>
            <Button variant="danger" size="xl" leftIcon={<Trash2 className="h-6 w-6" />}>
              Clear All
            </Button>
          </div>
        </div>
      </section>

      {/* Ghost Buttons */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Ghost Buttons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Small</h3>
            <Button variant="ghost" size="sm" leftIcon={<LogOut className="h-3 w-3" />}>
              Logout
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Medium</h3>
            <Button variant="ghost" size="md" leftIcon={<LogOut className="h-4 w-4" />}>
              Sign Out
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Large</h3>
            <Button variant="ghost" size="lg" leftIcon={<LogOut className="h-5 w-5" />}>
              Exit Session
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Extra Large</h3>
            <Button variant="ghost" size="xl" leftIcon={<LogOut className="h-6 w-6" />}>
              End Session
            </Button>
          </div>
        </div>
      </section>

      {/* Loading States */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Loading States</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Primary Loading</h3>
            <Button variant="primary" size="md" isLoading>
              Saving...
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Secondary Loading</h3>
            <Button variant="secondary" size="md" isLoading>
              Processing...
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Danger Loading</h3>
            <Button variant="danger" size="md" isLoading>
              Deleting...
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Outline Loading</h3>
            <Button variant="outline" size="md" isLoading>
              Loading...
            </Button>
          </div>
        </div>
      </section>

      {/* Disabled States */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Disabled States</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Primary Disabled</h3>
            <Button variant="primary" size="md" disabled leftIcon={<Plus className="h-4 w-4" />}>
              Add Item
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Secondary Disabled</h3>
            <Button variant="secondary" size="md" disabled leftIcon={<Save className="h-4 w-4" />}>
              Save
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Danger Disabled</h3>
            <Button variant="danger" size="md" disabled leftIcon={<Trash2 className="h-4 w-4" />}>
              Delete
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Outline Disabled</h3>
            <Button variant="outline" size="md" disabled leftIcon={<Eye className="h-4 w-4" />}>
              View
            </Button>
          </div>
        </div>
      </section>

      {/* Full Width Buttons */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Full Width Buttons</h2>
        <div className="space-y-4">
          <Button variant="primary" size="lg" fullWidth leftIcon={<Plus className="h-5 w-5" />}>
            Create New Session
          </Button>
          <Button variant="outline" size="md" fullWidth leftIcon={<Eye className="h-4 w-4" />}>
            View All Sessions
          </Button>
          <Button variant="danger" size="md" fullWidth leftIcon={<Trash2 className="h-4 w-4" />}>
            Delete All Sessions
          </Button>
        </div>
      </section>

      {/* Icon Only Buttons */}
      <section className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Icon Only Buttons</h2>
        <div className="flex flex-wrap gap-4">
          <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />} />
          <Button variant="secondary" size="sm" leftIcon={<Settings className="h-4 w-4" />} />
          <Button variant="outline" size="sm" leftIcon={<Eye className="h-4 w-4" />} />
          <Button variant="danger" size="sm" leftIcon={<Trash2 className="h-4 w-4" />} />
          <Button variant="ghost" size="sm" leftIcon={<LogOut className="h-4 w-4" />} />
        </div>
      </section>
    </div>
  );
};

export default ButtonShowcase;
